import { useEffect, useMemo, useState } from 'react'
import { OUR_TEAM_ID } from '../lib/club'

/**
 * Team crest with a remote-first lookup + on-disk fallback + initial fallback.
 *
 * Resolution order (first that loads wins):
 *   1. Remote bucket (R2/CDN) — when `VITE_CRESTS_BASE_URL` is set we try
 *      `<base>/<team_id>.<ext>` for png → svg → webp → jpg.
 *   2. Local files in `frontend/public/crests/<team_id>.<ext>` (same ext order).
 *   3. A coloured disc with the team's first initial, so the layout never
 *      looks broken when a crest hasn't been wired up yet.
 *
 * Point the bucket at R2 by setting in `frontend/.env`:
 *   VITE_CRESTS_BASE_URL=https://pub-xxxx.r2.dev/crests
 * (no trailing slash needed — we normalise). See public/crests/README.md.
 */

interface Props {
  teamId: number
  teamName: string
  /** px (liczba) albo dowolna długość CSS — np. `'100%'` / `clamp(...)`, gdy herb
   *  ma się skalować z kontenerem (punkty wykresu w Rankingu). */
  size?: number | string
  /** Background tone for the fallback disc only — accepts any CSS color. Domyślnie
   *  czerwień klubu dla NAS i neutralny grafit dla reszty (inicjał rywala w barwach
   *  Pogoni czytałby się jak nasz). */
  fallbackColor?: string
  /** Herb na ciemnym (czerwonym) tle — nasz inicjał wtedy biały krąg z czerwoną literą. */
  onDark?: boolean
  className?: string
  /** Fires once the crest has settled (image loaded, or all candidates exhausted →
   *  initial disc). Used by print routes to gate the "ready-to-capture" flag so a
   *  slow remote crest never lands after Playwright grabs the page. */
  onReady?: () => void
}

const EXTS = ['png', 'svg', 'webp', 'jpg'] as const

// Trim a trailing slash so we don't emit `base//42.png`.
const REMOTE_BASE = (import.meta.env.VITE_CRESTS_BASE_URL as string | undefined)?.replace(/\/+$/, '')

/** Ordered list of candidate URLs to try for a team id. */
function candidates(teamId: number): string[] {
  const out: string[] = []
  if (REMOTE_BASE) {
    for (const ext of EXTS) out.push(`${REMOTE_BASE}/${teamId}.${ext}`)
  }
  for (const ext of EXTS) out.push(`/crests/${teamId}.${ext}`)
  return out
}

export function TeamCrest({
  teamId,
  teamName,
  size = 64,
  fallbackColor,
  onDark = false,
  className,
  onReady,
}: Props) {
  const ours = teamId === OUR_TEAM_ID
  const discBg = onDark && ours ? '#FFFFFF' : fallbackColor ?? (ours ? '#74111B' : '#505054')
  const discFg = onDark && ours ? '#74111B' : '#FFFFFF'
  const urls = useMemo(() => candidates(teamId), [teamId])
  const [idx, setIdx] = useState(0)
  const exhausted = idx >= urls.length

  // No candidate loaded (or none configured) → the initial disc is the final
  // state; signal readiness so a print gate doesn't wait forever.
  useEffect(() => {
    if (exhausted) onReady?.()
  }, [exhausted, onReady])

  if (exhausted) {
    const initial = teamName.trim().charAt(0).toUpperCase() || '?'
    return (
      <div
        aria-label={teamName}
        title={teamName}
        className={
          'flex shrink-0 items-center justify-center rounded-full font-bold shadow-inner ' +
          (className ?? '')
        }
        style={{
          width: size,
          height: size,
          background: discBg,
          color: discFg,
          fontSize: typeof size === 'number' ? size * 0.42 : `calc(${size} * 0.42)`,
          letterSpacing: '-0.02em',
        }}
      >
        {initial}
      </div>
    )
  }

  const src = urls[idx]
  return (
    <img
      key={src}
      src={src}
      alt={teamName}
      title={teamName}
      width={typeof size === 'number' ? size : undefined}
      height={typeof size === 'number' ? size : undefined}
      className={'shrink-0 object-contain ' + (className ?? '')}
      style={{ width: size, height: size }}
      onLoad={() => onReady?.()}
      onError={() => setIdx((i) => i + 1)}
    />
  )
}
